import {
  Injectable,
  EventEmitter,
  ComponentFactory,
  ComponentRef
} from "@angular/core";

import * as _ from "lodash";
import { Subscription } from "rxjs";

import { DialogBaseService } from "./dialog.base.service";

import { DialogComponent } from "./components/dialog/dialog.component";
import { DialogHeaderComponent } from "./components/partials/header/header.component";
import { DialogFooterComponent } from "./components/partials/footer/footer.component";

import { FormValues, FormSchemaData } from "../form/form.interface";

export class Dialog {
  /**
   * @ignore
   */
  constructor(
    private _service: DialogBaseService,
    private _data: DialogInjectData,
    public index: number
  ) {
    _service.dialogs.push(this);
    this._updateParams();
  }

  public destroyed: boolean = false;
  public componentRef: ComponentRef<any>;
  public componentInstance: DialogComponent;
  public headerInstances: Array<DialogHeaderComponent> = [];
  public footerInstances: Array<DialogFooterComponent> = [];

  public onShow: EventEmitter<any> = new EventEmitter();
  public onShown: EventEmitter<any> = new EventEmitter();

  public onHide: EventEmitter<any> = new EventEmitter();
  public onHidden: EventEmitter<any> = new EventEmitter();

  public onOpen: EventEmitter<any> = new EventEmitter();
  public onDismiss: EventEmitter<any> = new EventEmitter();
  public onClose: Promise<any> = new Promise(
    resolve => (this._onClosePromise = resolve)
  );

  public onReturnValue: EventEmitter<any> = new EventEmitter();
  public onParamsChange: EventEmitter<DialogParams> = new EventEmitter();
  public onSettingsChange: EventEmitter<DialogSettings> = new EventEmitter();

  public $onResizeRequested: EventEmitter<DialogSettings> = new EventEmitter();
  public onResize: EventEmitter<DialogSettings> = new EventEmitter();

  public $onHeaderListChange: EventEmitter<
    Array<DialogHeaderComponent>
  > = new EventEmitter();
  public $onFooterListChange: EventEmitter<
    Array<DialogFooterComponent>
  > = new EventEmitter();

  public $onReady: EventEmitter<any> = new EventEmitter();

  private _params: DialogParams = new DialogParams();
  private _onClosePromise: (value?: any) => void;

  public get visible(): boolean {
    return this.ready ? this.componentInstance.visible : false;
  }
  public set visible(visible: boolean) {
    if (this.ready) this.componentInstance.visible = visible;
  }

  public get ready(): boolean {
    return this.componentInstance ? this.componentInstance.ready : false;
  }
  public set ready(ready: boolean) {
    if (this.componentInstance) this.componentInstance.ready = ready;
  }

  public get component(): any {
    return this._data.component;
  }
  public get params(): DialogParams {
    return this._params;
  }
  public get settings(): DialogSettings {
    let settings: DialogSettings = this._data.settings;
    if (!settings) settings = {};

    if (!(settings.backdrop && settings.backdrop.onClick)) {
      if (!settings.backdrop) settings.backdrop = {};
      settings.backdrop.onClick = "close";
    }

    if (
      !(
        settings.header &&
        settings.header.closable &&
        settings.header.closeClick
      )
    ) {
      if (!settings.header) settings.header = { title: null };
      if (settings.header.closable) settings.header.closeClick = "close";
    }

    if (!_.isBoolean(settings.fit_to_screen)) {
      settings.fit_to_screen = true;
    }

    return settings;
  }
  public get zindex(): number {
    return this._data.zindex;
  }
  public get content_zindex(): number {
    return this._data.content_zindex;
  }
  public get backdrop_zindex(): number {
    return this._data.backdrop_zindex;
  }

  public get fit_to_screen(): boolean {
    return this.settings.fit_to_screen;
  }

  public get wrapper_class(): string {
    return this.settings.wrapper_class || "";
  }

  public get dialog_class(): string {
    return this.settings.dialog_class || "";
  }

  public get content_class(): string {
    return this.settings.content_class || "";
  }

  public get backdrop_class(): string {
    return this.settings.backdrop_class || "";
  }

  public setSettings(settings: DialogSettings) {
    this._data.settings = Object.assign(
      {},
      this._data.settings || {},
      settings
    );
    this.onSettingsChange.emit(this._data.settings);
  }

  public setSize(size: DialogSettingsSize) {
    if (!this._data.settings) this._data.settings = {};
    this._data.settings.size = size;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setBackdrop(backdrop: DialogSettingsBackdrop) {
    if (!this._data.settings) this._data.settings = {};
    this._data.settings.backdrop = backdrop;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setKeyboard(keyboard: DialogSettingsKeyboard) {
    if (!this._data.settings) this._data.settings = {};
    this._data.settings.keyboard = keyboard;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setHeader(header: DialogSettingsHeader) {
    if (!this._data.settings) this._data.settings = {};
    this._data.settings.header = header;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setParams(params: DialogParamsData) {
    this._data.params = params;
    this._updateParams();
    this.onParamsChange.emit(this._params);
  }

  public resize() {
    this.$onResizeRequested.emit();
  }

  public sizeClass(): string {
    switch (this.settings.size) {
      case "xs":
        return "modal-xs";
      case "sm":
        return "modal-sm";
      case "md":
        return "modal-md";
      case "lg":
        return "modal-lg";
      case "responsive":
        return "modal-responsive";
      default:
        return "modal-md";
    }
  }

  public hasHeader(): boolean {
    return this.headerInstances.length > 0;
  }

  public hasFooter(): boolean {
    return this.footerInstances.length > 0;
  }

  public getOffsetHeight(): number {
    let height = 0;
    let gutter = 0;
    const content = this.componentInstance.dialogContent.nativeElement;
    const contentStyle =
      content.currentStyle || window.getComputedStyle(content);
    const attrs = ["margin", "padding"];

    attrs.forEach(
      attr =>
        (gutter +=
          parseInt(contentStyle[`${attr}Top`]) +
          parseInt(contentStyle[`${attr}Bottom`]))
    );

    this.headerInstances.forEach(
      component => (height += component.elementRef.nativeElement.offsetHeight)
    );
    this.footerInstances.forEach(
      component => (height += component.elementRef.nativeElement.offsetHeight)
    );

    return height + gutter;
  }

  public addHeader(header: DialogHeaderComponent): number {
    const index = this.headerInstances.length;

    this.headerInstances.push(header);
    this.$onHeaderListChange.emit(this.headerInstances);

    return index;
  }

  public removeHeader(index: number): boolean {
    if (this.headerInstances[index]) {
      this.headerInstances.splice(index, 1);
      this.$onHeaderListChange.emit(this.headerInstances);
      return true;
    }

    return false;
  }

  public addFooter(header: DialogFooterComponent): number {
    const index = this.footerInstances.length;

    this.footerInstances.push(header);
    this.$onFooterListChange.emit(this.footerInstances);

    return index;
  }

  public removeFooter(index: number): boolean {
    if (this.footerInstances[index]) {
      this.footerInstances.splice(index, 1);
      this.$onFooterListChange.emit(this.footerInstances);
      return true;
    }

    return false;
  }

  public show() {
    if (this.destroyed) {
      console.warn("Is't possible to open a destroyed dialog.");
      return;
    }

    if (this.ready) {
      this.onOpen.emit();
      this.onShow.emit();
      this._service.onShow.emit(this);
      this.componentInstance.visible = true;
    } else {
      const ready_subscription = this.$onReady.subscribe(() => {
        this.show();
        ready_subscription.unsubscribe();
      });
    }
  }

  public open() {
    this.show();
  }

  public dismiss() {
    if (this.ready) {
      const hidden_subscription: Subscription = this.onHidden.subscribe(() => {
        hidden_subscription.unsubscribe();
        this.onDismiss.emit();
        this._service.onHide.emit(this);
      });
      this.componentInstance.visible = false;
    } else {
      const ready_subscription = this.$onReady.subscribe(() => {
        this.dismiss();
        ready_subscription.unsubscribe();
      });
    }
  }

  public close() {
    if (this.ready) {
      const hidden_subscription: Subscription = this.onHidden.subscribe(() => {
        hidden_subscription.unsubscribe();
        this.onDismiss.emit();
        this._service.onHide.emit(this);
        this._destroy();
      });
      this.componentInstance.visible = false;
    } else {
      const ready_subscription = this.$onReady.subscribe(() => {
        this.close();
        ready_subscription.unsubscribe();
      });
    }
  }

  public returnValue(response: any) {
    this.onReturnValue.emit(response);
  }

  private _updateParams() {
    this._params.params = this._data.params;
  }

  private _destroy() {
    let this_dialog_index: number;
    this._service.dialogs.forEach((dialog, index) => {
      if (dialog.index == this.index) this_dialog_index = index;
    });

    if (this_dialog_index >= 0) {
      this._service.dialogs.splice(this_dialog_index, 1);
      this._onClosePromise();
      this.destroyed = true;
    }
  }
}

@Injectable()
export class DialogRef {
  public dialog: Dialog;
}

@Injectable()
export class DialogParams {
  public params: DialogParamsData;
  public get(key: string) {
    return this.params ? this.params[key] : null;
  }
}

export type DialogSettingsSize = "xs" | "sm" | "md" | "lg" | "responsive";

export interface DialogSettingsBackdrop {
  onClick?: "close" | "dismiss" | "none";
}

export interface DialogSettingsKeyboard {
  onEscape?: "close" | "dismiss" | "none" | Function;
  onReturn?: "close" | "dismiss" | "none" | Function;
}

export interface DialogSettingsHeader {
  title: string;
  subtitle?: string;
  closable?: boolean;
  closeClick?: "close" | "dismiss";
}

export interface DialogSettings {
  size?: DialogSettingsSize;
  keyboard?: DialogSettingsKeyboard;
  backdrop?: DialogSettingsBackdrop;
  header?: DialogSettingsHeader;
  wrapper_class?: string;
  dialog_class?: string;
  content_class?: string;
  backdrop_class?: string;
  fit_to_screen?: boolean;
}

export interface DialogInjectData {
  component: ComponentFactory<any>;
  instance?: Dialog;
  params?: DialogParamsData;
  settings?: DialogSettings;
  zindex?: any;
  content_zindex?: any;
  backdrop_zindex?: any;
}

export interface DialogParamsData {
  [key: string]: any;
}

export interface DialogAlertParams {
  headerClass?: string;
  title?: string;
  titleClass?: string;
  subtitle?: string;
  subtitleClass?: string;
  message?: string;
  footerClass?: string;
  size?: "xs" | "sm" | "md" | "lg" | "responsive";
  keyboard?: DialogSettingsKeyboard;
  buttonText?: DialogStringFunction | string;
  buttonMode?: string;
  buttonColor?: string;
}

export interface DialogConfirmParams {
  headerClass?: string;
  title?: string;
  titleClass?: string;
  subtitle?: string;
  subtitleClass?: string;
  message?: string;
  footerClass?: string;
  size?: "xs" | "sm" | "md" | "lg" | "responsive";
  keyboard?: DialogSettingsKeyboard;
  revertButtonOrder?: boolean;
  resolveButtonText?: DialogStringFunction | string;
  resolveButtonMode?: string;
  resolveButtonColor?: string;
  rejectButtonText?: DialogStringFunction | string;
  rejectButtonMode?: string;
  rejectButtonColor?: string;
}

export interface DialogConfirmCallback {
  (response: boolean): void;
}

export interface DialogPromptParams {
  headerClass?: string;
  title: string;
  titleClass?: string;
  subtitle?: string;
  subtitleClass?: string;
  contentBefore?: string;
  contentAfter?: string;
  schema: Array<FormSchemaData> | FormSchemaData;
  footerClass?: string;
  size?: "xs" | "sm" | "md" | "lg" | "responsive";
  revertButtonOrder?: boolean;
  resolveButtonText?: DialogStringFunction | string;
  resolveButtonMode?: string;
  resolveButtonColor?: string;
  rejectButtonText?: DialogStringFunction | string;
  rejectButtonMode?: string;
  rejectButtonColor?: string;
}

export interface DialogPromptCallback {
  (response: FormValues): void;
}

export type DialogStringFunction = () => string;
